// PRD §2.1 / §3.1 — recursive tree row. Renders Domain → SubArea → Item nodes
// with chevron expand/collapse, click-to-select highlight, auto-numbering badge,
// and the schedule + status badges on item rows.
//
// Slice 5 added hover-row actions for DomainRow (+ Add Sub-area / ↑ / ↓ / 🗑).
// Slice 6 added hover-row actions for SubAreaRow (+ Add Item / ↑ / ↓ / 🗑).
// Slice 7+ will add item-row hover actions once the item detail form is ready.

import { useState, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ChevronDown,
  ChevronRight,
  ChevronUp,
  ChevronDown as ChevronDownArrow,
  Plus,
  Trash2,
} from 'lucide-react';

import { ConfirmModal } from '@/components/feedback/ConfirmModal';
import { toast } from '@/components/feedback/useToast';
import type { ProDomain, ProItemDesignApiError } from '../types/pro-item-design.types';
import {
  domainKey,
  nextTempId,
  subAreaKey,
  useProItemDesignPageStore,
  type SelectedNode,
} from '../store/pro-item-design-page.store';
import { useProTreeQuery } from '../hooks/use-pro-tree';
import {
  useDeleteDomain,
  useDeleteSubArea,
  useReorder,
} from '../hooks/use-pro-mutations';
import { isDraftId } from '../utils/draft-node';
import { ProStatusBadge } from './ProStatusBadge';
import { DirtyBadge } from '@/components/feedback/DirtyBadge';
import { useWriteGuard } from '@/shared/hooks/useWriteGuard';

const ROW_BASE: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  padding: '5px 8px',
  borderRadius: 6,
  cursor: 'pointer',
  fontSize: 13,
  userSelect: 'none',
};

const ROW_SELECTED: React.CSSProperties = {
  background: 'var(--badge-primary-bg)',
};

const NUM_STYLE: React.CSSProperties = {
  color: 'var(--info)',
  fontWeight: 600,
  flexShrink: 0,
  marginRight: 4,
  fontSize: 11,
};

const META_STYLE: React.CSSProperties = {
  fontSize: 11,
  color: 'var(--muted-foreground)',
  flexShrink: 0,
  marginLeft: 4,
  whiteSpace: 'nowrap',
};

const LABEL_STYLE: React.CSSProperties = {
  flex: 1,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const TOG_STYLE: React.CSSProperties = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'var(--muted-foreground)',
  flexShrink: 0,
  width: 18,
  height: 18,
};

const SPACER_STYLE: React.CSSProperties = { width: 18, flexShrink: 0 };

// Tiny icon button used inside the hover-action group. Stops click propagation
// so it doesn't also select the row underneath.
function ActionButton({
  title,
  onClick,
  disabled,
  children,
  ...rest
}: {
  title: string;
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
  // SHCT-582 — the P-2/P-3 pair arrives as a spread (`data-perm-denied` + `title`), and a
  // component that swallows unknown props would drop the marker silently: the tooltip would still
  // show and the L2 guard would never fire, which is the half-applied state the ticket is about.
} & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      {...rest}
      title={title}
      aria-label={title}
      onClick={(e) => {
        e.stopPropagation();
        if (!disabled) onClick();
      }}
      disabled={disabled}
      style={{
        background: 'none',
        border: 'none',
        cursor: disabled ? 'not-allowed' : 'pointer',
        padding: 2,
        borderRadius: 3,
        color: 'var(--muted-foreground)',
        display: 'flex',
        alignItems: 'center',
        opacity: disabled ? 0.4 : 1,
      }}
    >
      {children}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DomainRow
// ─────────────────────────────────────────────────────────────────────────────

interface DomainRowProps {
  domain: ProDomain;
  domainIndex: number;
  // Sibling list (entire tree) — used for reorder bookkeeping.
  siblings: readonly ProDomain[];
}

export function DomainRow({ domain, domainIndex, siblings }: DomainRowProps) {
  const { t } = useTranslation('pro-item-design');
  const { canWrite, denyProps, tooltip } = useWriteGuard('PRO_MGMT');
  const selected = useProItemDesignPageStore((s) => s.selected);
  const expandedMap = useProItemDesignPageStore((s) => s.expanded);
  const toggleExpand = useProItemDesignPageStore((s) => s.toggleExpand);
  const setExpanded = useProItemDesignPageStore((s) => s.setExpanded);
  const select = useProItemDesignPageStore((s) => s.select);
  const requestSelect = useProItemDesignPageStore((s) => s.requestSelect);
  const requestFocus = useProItemDesignPageStore((s) => s.requestFocus);
  const startDraft = useProItemDesignPageStore((s) => s.startDraft);
  const clearDraft = useProItemDesignPageStore((s) => s.clearDraft);

  const deleteDomain = useDeleteDomain();
  const reorder = useReorder();

  const [confirmOpen, setConfirmOpen] = useState(false);

  const key = domainKey(domain.id);
  const isExpanded = !!expandedMap[key];
  const isSelected = selected?.type === 'domain' && selected.id === domain.id;
  const canMoveUp = domainIndex > 0;
  const canMoveDown = domainIndex < siblings.length - 1;

  const onToggle = (e: MouseEvent) => {
    e.stopPropagation();
    toggleExpand(key);
  };
  const onSelect = () => {
    requestSelect({ type: 'domain', id: domain.id });
  };

  // SHCT-388 — park a draft sub-area; persisted on Save (see DomainDetail).
  const onAddSubArea = () => {
    const tempId = nextTempId();
    startDraft({
      type: 'subArea',
      tempId,
      domainId: domain.id,
      name: t('defaults.newSubAreaName'),
    });
    setExpanded(key, true);
    setExpanded(subAreaKey(tempId), true);
    select({ type: 'subArea', id: tempId, domainId: domain.id });
    requestFocus(tempId);
  };

  const onMove = (direction: -1 | 1) => {
    // SHCT-388 — the unsaved draft (negative id) is appended last and is not a
    // real sibling; exclude it so reorder only ships persisted ids.
    const ids = siblings.map((d) => d.id).filter((id) => !isDraftId(id));
    const j = domainIndex + direction;
    if (j < 0 || j >= ids.length) return;
    [ids[domainIndex], ids[j]] = [ids[j], ids[domainIndex]];
    reorder.mutate(
      { kind: 'DOMAIN', parentId: null, orderedIds: ids },
      {
        onError: (e) => {
          const err = e as ProItemDesignApiError;
          toast.error(err?.message || t('toasts.reorderFailed'));
        },
      },
    );
  };

  const onConfirmDelete = () => {
    if (isDraftId(domain.id)) {
      // SHCT-388 — draft Area was never persisted; delete just discards it.
      setConfirmOpen(false);
      clearDraft();
      select(null);
      return;
    }
    deleteDomain.mutate(domain.id, {
      onSuccess: () => {
        setConfirmOpen(false);
        toast.success(t('toasts.deleted'));
        if (selected && 'domainId' in selected && selected.domainId === domain.id) {
          select(null);
        } else if (selected?.type === 'domain' && selected.id === domain.id) {
          select(null);
        }
      },
      onError: (e) => {
        setConfirmOpen(false);
        const err = e as ProItemDesignApiError;
        toast.error(err?.message || t('toasts.deleteFailed'));
      },
    });
  };

  return (
    <div>
      <div
        className="group"
        style={isSelected ? { ...ROW_BASE, ...ROW_SELECTED } : ROW_BASE}
        onClick={onSelect}
        role="treeitem"
        aria-selected={isSelected}
        aria-expanded={isExpanded}
      >
        <button type="button" style={TOG_STYLE} onClick={onToggle} aria-label="toggle">
          {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>
        <span style={NUM_STYLE}>{domainIndex + 1}.</span>
        <span style={{ ...LABEL_STYLE, fontWeight: 600 }}>{domain.name}</span>
        <div className="hidden group-hover:flex" style={{ marginLeft: 'auto', gap: 2 }}>
          <ActionButton
            onClick={onAddSubArea}
            disabled={!canWrite || isDraftId(domain.id)}
            {...denyProps}
            title={tooltip ?? t('actions.addSubArea')}
          >
            <Plus size={13} />
          </ActionButton>
          <ActionButton
            onClick={() => onMove(-1)}
            disabled={!canWrite || !canMoveUp || isDraftId(domain.id)}
            {...denyProps}
            title={tooltip ?? t('actions.moveUp')}
          >
            <ChevronUp size={13} />
          </ActionButton>
          <ActionButton
            onClick={() => onMove(1)}
            disabled={!canWrite || !canMoveDown || isDraftId(domain.id)}
            {...denyProps}
            title={tooltip ?? t('actions.moveDown')}
          >
            <ChevronDownArrow size={13} />
          </ActionButton>
          <ActionButton
            onClick={() => setConfirmOpen(true)}
            disabled={!canWrite}
            {...denyProps}
            title={tooltip ?? t('actions.delete')}
          >
            <Trash2 size={13} />
          </ActionButton>
        </div>
      </div>
      {isExpanded ? (
        <div style={{ paddingLeft: 20 }}>
          {domain.subAreas.map((s, si) => (
            <SubAreaRow
              key={s.id}
              subArea={s}
              domainId={domain.id}
              domainIndex={domainIndex}
              subAreaIndex={si}
              siblings={domain.subAreas}
            />
          ))}
        </div>
      ) : null}

      <ConfirmModal
        open={confirmOpen}
        title={t('domain.delete.title')}
        message={t('domain.delete.message')}
        confirmLabel={t('domain.delete.confirm')}
        cancelLabel={t('actions.cancel')}
        confirmVariant="destructive"
        loading={deleteDomain.isPending}
        loadingLabel={t('actions.deleting')}
        onConfirm={onConfirmDelete}
        onCancel={() => setConfirmOpen(false)}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SubAreaRow
// ─────────────────────────────────────────────────────────────────────────────

interface SubAreaRowProps {
  subArea: ProDomain['subAreas'][number];
  domainId: number;
  domainIndex: number;
  subAreaIndex: number;
  siblings: readonly ProDomain['subAreas'][number][];
}

function SubAreaRow({
  subArea,
  domainId,
  domainIndex,
  subAreaIndex,
  siblings,
}: SubAreaRowProps) {
  const { t } = useTranslation('pro-item-design');
  const { canWrite, denyProps, tooltip } = useWriteGuard('PRO_MGMT');
  const selected = useProItemDesignPageStore((s) => s.selected);
  const expandedMap = useProItemDesignPageStore((s) => s.expanded);
  const toggleExpand = useProItemDesignPageStore((s) => s.toggleExpand);
  const setExpanded = useProItemDesignPageStore((s) => s.setExpanded);
  const select = useProItemDesignPageStore((s) => s.select);
  const requestSelect = useProItemDesignPageStore((s) => s.requestSelect);
  const requestFocus = useProItemDesignPageStore((s) => s.requestFocus);
  const startDraft = useProItemDesignPageStore((s) => s.startDraft);
  const clearDraft = useProItemDesignPageStore((s) => s.clearDraft);

  const deleteSubArea = useDeleteSubArea();
  const reorder = useReorder();

  const [confirmOpen, setConfirmOpen] = useState(false);

  const key = subAreaKey(subArea.id);
  const isExpanded = !!expandedMap[key];
  const isSelected = selected?.type === 'subArea' && selected.id === subArea.id;
  const num = `${domainIndex + 1}-${subAreaIndex + 1}`;
  const canMoveUp = subAreaIndex > 0;
  const canMoveDown = subAreaIndex < siblings.length - 1;

  const onToggle = (e: MouseEvent) => {
    e.stopPropagation();
    toggleExpand(key);
  };
  const onSelect = () => {
    const node: SelectedNode = { type: 'subArea', id: subArea.id, domainId };
    requestSelect(node);
  };

  // SHCT-388 — park a draft item; persisted on Save (see ItemDetail two-step).
  const onAddItem = () => {
    const tempId = nextTempId();
    startDraft({
      type: 'item',
      tempId,
      domainId,
      subAreaId: subArea.id,
      name: t('defaults.newItemName'),
    });
    setExpanded(domainKey(domainId), true);
    setExpanded(key, true);
    select({ type: 'item', id: tempId, domainId, subAreaId: subArea.id });
    requestFocus(tempId);
  };

  const onMove = (direction: -1 | 1) => {
    // SHCT-388 — exclude the unsaved draft (negative id, appended last).
    const ids = siblings.map((s) => s.id).filter((id) => !isDraftId(id));
    const j = subAreaIndex + direction;
    if (j < 0 || j >= ids.length) return;
    [ids[subAreaIndex], ids[j]] = [ids[j], ids[subAreaIndex]];
    reorder.mutate(
      { kind: 'SUB_AREA', parentId: domainId, orderedIds: ids },
      {
        onError: (e) => {
          const err = e as ProItemDesignApiError;
          toast.error(err?.message || t('toasts.reorderFailed'));
        },
      },
    );
  };

  const onConfirmDelete = () => {
    if (isDraftId(subArea.id)) {
      // SHCT-388 — draft Sub-area was never persisted; delete just discards it.
      setConfirmOpen(false);
      clearDraft();
      select(null);
      return;
    }
    deleteSubArea.mutate(subArea.id, {
      onSuccess: () => {
        setConfirmOpen(false);
        toast.success(t('toasts.deleted'));
        if (
          selected &&
          ((selected.type === 'subArea' && selected.id === subArea.id) ||
            (selected.type === 'item' && selected.subAreaId === subArea.id))
        ) {
          select(null);
        }
      },
      onError: (e) => {
        setConfirmOpen(false);
        const err = e as ProItemDesignApiError;
        toast.error(err?.message || t('toasts.deleteFailed'));
      },
    });
  };

  return (
    <div>
      <div
        className="group"
        style={isSelected ? { ...ROW_BASE, ...ROW_SELECTED } : ROW_BASE}
        onClick={onSelect}
        role="treeitem"
        aria-selected={isSelected}
        aria-expanded={isExpanded}
      >
        <button type="button" style={TOG_STYLE} onClick={onToggle} aria-label="toggle">
          {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>
        <span style={NUM_STYLE}>{num}.</span>
        <span style={LABEL_STYLE}>{subArea.name}</span>
        <span style={META_STYLE}>({subArea.items.length})</span>
        <div className="hidden group-hover:flex" style={{ marginLeft: 4, gap: 2 }}>
          <ActionButton
            onClick={onAddItem}
            disabled={!canWrite || isDraftId(subArea.id)}
            {...denyProps}
            title={tooltip ?? t('actions.addItem')}
          >
            <Plus size={13} />
          </ActionButton>
          <ActionButton
            onClick={() => onMove(-1)}
            disabled={!canWrite || !canMoveUp || isDraftId(subArea.id)}
            {...denyProps}
            title={tooltip ?? t('actions.moveUp')}
          >
            <ChevronUp size={13} />
          </ActionButton>
          <ActionButton
            onClick={() => onMove(1)}
            disabled={!canWrite || !canMoveDown || isDraftId(subArea.id)}
            {...denyProps}
            title={tooltip ?? t('actions.moveDown')}
          >
            <ChevronDownArrow size={13} />
          </ActionButton>
          <ActionButton
            onClick={() => setConfirmOpen(true)}
            disabled={!canWrite}
            {...denyProps}
            title={tooltip ?? t('actions.delete')}
          >
            <Trash2 size={13} />
          </ActionButton>
        </div>
      </div>
      {isExpanded ? (
        <div style={{ paddingLeft: 20 }}>
          {subArea.items.map((item, ii) => (
            <ItemRow
              key={item.id}
              item={item}
              domainId={domainId}
              domainIndex={domainIndex}
              subAreaIndex={subAreaIndex}
              itemIndex={ii}
            />
          ))}
        </div>
      ) : null}

      <ConfirmModal
        open={confirmOpen}
        title={t('subArea.delete.title')}
        message={t('subArea.delete.message')}
        confirmLabel={t('subArea.delete.confirm')}
        cancelLabel={t('actions.cancel')}
        confirmVariant="destructive"
        loading={deleteSubArea.isPending}
        loadingLabel={t('actions.deleting')}
        onConfirm={onConfirmDelete}
        onCancel={() => setConfirmOpen(false)}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ItemRow — read-only display. Hover actions land in Slice 7+ when the item
// detail form is ready (so users have somewhere to land on Add Item).
// ─────────────────────────────────────────────────────────────────────────────

interface ItemRowProps {
  item: ProDomain['subAreas'][number]['items'][number];
  domainId: number;
  domainIndex: number;
  subAreaIndex: number;
  itemIndex: number;
}

// Exported for unit testing (FEATURE_028 / SHCT-590): this row owns the yellow 수정 badge,
// and the badge's whole point is that it must appear from the SERVER flag alone with an
// empty browser store. Reaching it through DomainRow would drag in the permission context
// and the tree query for no gain — this row needs neither, only two stores and a label.
export function ItemRow({ item, domainId, domainIndex, subAreaIndex, itemIndex }: ItemRowProps) {
  const { t } = useTranslation('pro-item-design');
  const selected = useProItemDesignPageStore((s) => s.selected);
  const requestSelect = useProItemDesignPageStore((s) => s.requestSelect);
  // PRD §3.4 — yellow "Edited" badge sticks until [PRO 배포] runs.
  //
  // FEATURE_025 — the server decides this marker (`item.dirty`) by comparing the item with its deployed
  // copy, so it is the same in a new tab and for another administrator. The browser's own list is no
  // longer OR-ed in: it could not tell a save that changed nothing, and it never cleared for another
  // disease's deploy, so it kept a badge the server had already dropped.
  const isDirty = item.dirty === true;

  const isSelected = selected?.type === 'item' && selected.id === item.id;
  const num = `${domainIndex + 1}-${subAreaIndex + 1}-${itemIndex + 1}`;

  const onSelect = () => {
    requestSelect({
      type: 'item',
      id: item.id,
      domainId,
      subAreaId: item.subAreaId,
    });
  };

  return (
    <div
      style={isSelected ? { ...ROW_BASE, ...ROW_SELECTED } : ROW_BASE}
      onClick={onSelect}
      role="treeitem"
      aria-selected={isSelected}
    >
      <span style={SPACER_STYLE} />
      <span style={NUM_STYLE}>{num}</span>
      {/* #6 — only the name truncates with an ellipsis; the serial + "수정"
          badge stay pinned and visible (they used to be clipped away by the
          name's overflow:hidden once the name got long). */}
      <span
        style={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          overflow: 'hidden',
        }}
      >
        <span
          style={{
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {item.name}
        </span>
        <span style={{ fontSize: 10, color: 'var(--muted-foreground)', flexShrink: 0 }}>
          {item.pid}
        </span>
        {isDirty ? (
          <DirtyBadge style={{ fontSize: 10, padding: '2px 6px', flexShrink: 0 }} />
        ) : null}
      </span>
      {item.scheduleSummary ? (
        <span style={META_STYLE}>{item.scheduleSummary}</span>
      ) : null}
      <ProStatusBadge status={item.status} />
    </div>
  );
}

// Re-export this helper hook (rather than calling it inside each row's parent)
// so the panel can pre-fetch and pass siblings down for the top-level DomainRow.
export { useProTreeQuery };
